/**
 * tenantPath.js — URL-path based multi-tenancy helpers.
 *
 * In production each tenant gets its own subdomain
 * (e.g. https://acme.agentic-intel.com/). For local dev we use a URL
 * path prefix instead: http://localhost:3030/<tenant_slug>/...
 *
 * The first path segment is treated as the tenant slug **unless** it
 * matches one of the reserved top-level app routes (`/data`, `/admin`,
 * …) — that way pre-existing slug-less URLs keep working for the
 * single-tenant dev case.
 *
 * SINGLE-TENANT MODE: the tenant slug is fixed and invisible. All helpers
 * return as if there is no slug in the URL.
 */
import { isSingleTenant } from '../services/deploymentConfigService';

// Org-id slug rules. The strict regex used when *creating* a new org via
// tenant-api/routers/orgs_router.py is `^[a-z][a-z0-9-]{1,62}$`, but
// seeded / legacy tenants (e.g. the Template Studio's `ee_studio`) can
// carry underscores. We accept the looser form here so those tenants
// also route correctly in the URL.
const SLUG_RE = /^[a-z][a-z0-9_-]{1,62}$/;

// Top-level paths that belong to the app itself, NOT to a tenant.
// Keep in sync with the Route definitions in App.js.
//
// NOTE: `studio` and `templates` are intentionally NOT reserved — they
// can legitimately be tenant slugs (e.g. the `studio` tenant). Their
// in-app counterparts (`/studio/templates`, `/templates`) remain
// reachable from the single-tenant (no-slug) URL form.
const RESERVED_FIRST_SEGMENTS = new Set([
  'data',
  'data-gallery',
  'admin',
  'pipeline',
  'verify-email',
  'api',
  'static',
]);

/**
 * Return the tenant slug encoded in the current URL path, or null when
 * the URL has no tenant prefix.
 */
export function getTenantSlugFromUrl() {
  if (isSingleTenant()) {return null;}
  if (typeof window === 'undefined') {return null;}
  const pathname = window.location.pathname || '/';
  const first = pathname.split('/').filter(Boolean)[0];
  if (!first) {return null;}
  if (RESERVED_FIRST_SEGMENTS.has(first)) {return null;}
  return SLUG_RE.test(first) ? first : null;
}

/**
 * Basename to feed into react-router's <BrowserRouter basename={...}>.
 * Returns '/<slug>' when the URL is tenant-prefixed, '' otherwise.
 */
export function getRouterBasename() {
  const slug = getTenantSlugFromUrl();
  return slug ? `/${slug}` : '';
}

/**
 * Build a tenant-rooted URL (used for full-page navigations that need
 * the router to remount with a new basename, e.g. after login).
 */
export function tenantUrl(slug, subPath = '/') {
  // Single-tenant: never emit a tenant-prefixed URL.
  if (isSingleTenant()) {
    return subPath && subPath.startsWith('/') ? subPath : `/${subPath || ''}`;
  }
  const safeSlug = (slug || '').trim().toLowerCase();
  if (!SLUG_RE.test(safeSlug)) {return subPath || '/';}
  const tail = subPath.startsWith('/') ? subPath : `/${subPath}`;
  return `/${safeSlug}${tail === '/' ? '/' : tail}`;
}

export function isValidSlug(value) {
  return typeof value === 'string' && SLUG_RE.test(value);
}
